import PropTypes from 'prop-types'

import {MicIcon, PauseIcon, PlayIcon} from './icons'
import {formatDuration} from './lifeStoryTools'
import {READ_MODES, RECORD_VERSION_COPY} from './hooks/useRecordVersion'
import {RECORDER_STATES} from './hooks/useVoiceRecorder'

const MODE_LABELS = {
  full: {[READ_MODES.SCRIPT]: 'Read it word for word', [READ_MODES.POINTS]: 'Use talking points'},
  short: {[READ_MODES.SCRIPT]: 'Word for word', [READ_MODES.POINTS]: 'Talking points'}
}

/**
 * The two ways to read the story while recording it (TK-3204 "Record this
 * version"). `short` is the takeover's narrower labelling. Switching is
 * off while a take is in progress.
 */
export const ReadModeSwitch = ({mode, disabled, short, className, onChange}) => {
  const labels = short ? MODE_LABELS.short : MODE_LABELS.full

  return (
    <div aria-label='How you want to read it' className={`ls-seg-btns${className ? ` ${className}` : ''}`} role='group'>
      {[READ_MODES.SCRIPT, READ_MODES.POINTS].map(option => (
        <button
          key={option}
          aria-pressed={mode === option}
          disabled={disabled}
          type='button'
          onClick={() => onChange(option)}
        >
          {labels[option]}
        </button>
      ))}
    </div>
  )
}

ReadModeSwitch.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  mode: PropTypes.oneOf(Object.values(READ_MODES)).isRequired,
  onChange: PropTypes.func.isRequired,
  short: PropTypes.bool
}

/** The story as a script, or as talking points (with the wait for them). */
export const ReadingBody = ({mode, text, points, pointsLoading}) => {
  if (mode === READ_MODES.SCRIPT) return <div className='ls-rv-script'>{text}</div>

  if (!points) {
    return (
      <div aria-live='polite' className='ls-iv-thinking ls-rv-thinking'>
        <span aria-hidden='true' className='ls-spin' />
        <span>{pointsLoading ? 'Pulling out the key moments\u2026' : 'Nothing to read yet.'}</span>
      </div>
    )
  }

  return (
    <ul className='ls-rv-points'>
      {points.map((point, index) => <li key={`${index}-${point}`}>{point}</li>)}
    </ul>
  )
}

ReadingBody.propTypes = {
  mode: PropTypes.oneOf(Object.values(READ_MODES)).isRequired,
  points: PropTypes.arrayOf(PropTypes.string),
  pointsLoading: PropTypes.bool,
  text: PropTypes.string.isRequired
}

export const recordLabel = ({state, duration}) => {
  if (state === RECORDER_STATES.PROCESSING) return 'Saving your recording\u2026'
  if (state === RECORDER_STATES.RECORDING || state === RECORDER_STATES.PAUSED) return `Stop recording \u00b7 ${formatDuration(duration)}`

  return 'Start recording'
}

export const readingHint = ({mode, state}) => {
  if (state === RECORDER_STATES.PAUSED) return RECORD_VERSION_COPY.hints.paused

  return mode === READ_MODES.POINTS ? RECORD_VERSION_COPY.hints.points : RECORD_VERSION_COPY.hints.script
}

/**
 * Start/Stop with the running time, plus Pause/Resume once a take is
 * going (after it, as in the modal, or before it with `pauseFirst`, as in
 * the takeover's footer). `big` is the takeover's sizing; `speakRef`
 * reaches the main button so the surfaces can focus it on open.
 */
export const RecordControls = ({recorder, big, pauseFirst, speakRef, className}) => {
  const live = recorder.state === RECORDER_STATES.RECORDING || recorder.state === RECORDER_STATES.PAUSED
  const paused = recorder.state === RECORDER_STATES.PAUSED
  const processing = recorder.state === RECORDER_STATES.PROCESSING

  const pauseButton = live && (
    <button
      aria-label={paused ? 'Resume recording' : 'Pause recording'}
      className={`ls-pause-btn${big ? ' big' : ''}`}
      type='button'
      onClick={paused ? recorder.resume : recorder.pause}
    >
      {paused ? <PlayIcon size={16} /> : <PauseIcon size={16} />}
      <span>{paused ? 'Resume' : 'Pause'}</span>
    </button>
  )

  return (
    <>
      {pauseFirst && pauseButton}
      <button
        aria-pressed={live}
        className={`${className}${live ? ' recording' : ''}${paused ? ' rec-paused' : ''}`}
        disabled={processing || !recorder.supported}
        ref={speakRef}
        type='button'
        onClick={recorder.toggle}
      >
        {!live && !processing && <MicIcon size={17} />}
        {live && !paused && <span aria-hidden='true' className='ls-rec-dot' />}
        {processing && <span aria-hidden='true' className='ls-spin' />}
        <span>{recordLabel(recorder)}</span>
      </button>
      {!pauseFirst && pauseButton}
    </>
  )
}

const recorderShape = PropTypes.shape({
  duration: PropTypes.number.isRequired,
  pause: PropTypes.func.isRequired,
  resume: PropTypes.func.isRequired,
  state: PropTypes.string.isRequired,
  supported: PropTypes.bool,
  toggle: PropTypes.func.isRequired
})

RecordControls.propTypes = {
  big: PropTypes.bool,
  className: PropTypes.string.isRequired,
  pauseFirst: PropTypes.bool,
  recorder: recorderShape.isRequired,
  speakRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})])
}
